import React, { useState, useEffect, useContext, useRef } from 'react';
import { Mic, Square, Upload, Loader, Pause, Play } from 'lucide-react';
import { UserContext } from './UserContext';
import '../styles/Doubts.css';

const Doubts = () => {
  const { user } = useContext(UserContext);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousDoubts, setPreviousDoubts] = useState([]);
  const [error, setError] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioElementsRef = useRef({});

  useEffect(() => {
    fetchPreviousDoubts();
    return () => {
      // Cleanup function to pause all audio and remove event listeners
      Object.values(audioElementsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          audio.removeEventListener('ended', audio.onEnded);
          audio.removeEventListener('error', audio.onError);
        }
      });
      audioElementsRef.current = {};
    };
  }, [user]);

  const fetchPreviousDoubts = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`https://quiz-server-ghre.onrender.com/api/doubts/user/${user}`);
      if (!response.ok) throw new Error('Failed to fetch doubts');
      const data = await response.json();
      
      const sortedDoubts = data.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setPreviousDoubts(sortedDoubts);

      // Initialize audio elements for each doubt
      sortedDoubts.forEach(doubt => {
        const audio = new Audio(doubt.audioUrl);
        
        // Store the event handler functions on the audio element itself
        audio.onEnded = () => {
          console.log('Audio ended:', doubt.audioUrl);
          setCurrentlyPlaying(null);
        };
        
        audio.onError = () => {
          console.error('Audio error:', doubt.audioUrl);
          setError('Failed to play audio');
          setCurrentlyPlaying(null);
        };

        audio.addEventListener('ended', audio.onEnded);
        audio.addEventListener('error', audio.onError);
        
        audioElementsRef.current[doubt.audioUrl] = audio;
      });
    } catch (err) {
      console.error('Error fetching doubts:', err);
      setError('Failed to load previous doubts');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      recorder.start();
      setIsRecording(true);
      setMediaRecorder(recorder);
      setError(null);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const uploadRecording = async () => {
    if (!audioBlob) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.wav');
    formData.append('username', user);

    try {
      const response = await fetch('https://quiz-server-ghre.onrender.com/api/doubts/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to upload recording');
      }

      // Refresh the doubts list
      const doubtsResponse = await fetch(`https://quiz-server-ghre.onrender.com/api/doubts/user/${user}`);
      const doubtsData = await doubtsResponse.json();
      const sortedDoubts = doubtsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setPreviousDoubts(sortedDoubts);

      // Clear the current recording
      setAudioBlob(null);
      setAudioUrl(null);
      setError(null);

      // Show success message
      setError({ type: 'success', message: 'Recording uploaded successfully!' });
    } catch (err) {
      console.error('Error uploading recording:', err);
      setError(err.message || 'Failed to upload recording. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePlayPause = (audioUrl) => {
    const audio = audioElementsRef.current[audioUrl];
    if (!audio) return;

    if (currentlyPlaying === audioUrl) {
      // Pause current audio
      audio.pause();
      setCurrentlyPlaying(null);
    } else {
      // Pause any playing audio
      if (currentlyPlaying && audioElementsRef.current[currentlyPlaying]) {
        audioElementsRef.current[currentlyPlaying].pause();
      }
      // Play new audio
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
        setError('Failed to play audio');
        setCurrentlyPlaying(null);
      });
      setCurrentlyPlaying(audioUrl);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="doubts-container">
      <h1>Post Your Doubt</h1>

      <div className="recording-section">
        <div className="record-controls">
          {!isRecording ? (
            <div className="record-button-container">
              <button
                className="record-button"
                onClick={startRecording}
                disabled={isUploading}
                aria-label="Start Recording"
              >
                <Mic size={24} />
              </button>
              <span className="record-label">Start Recording</span>
            </div>
          ) : (
            <div className="record-button-container">
              <button
                className="stop-button"
                onClick={stopRecording}
                disabled={isUploading}
                aria-label="Stop Recording"
              >
                <Square size={24} />
              </button>
              <span className="record-label stop">Stop Recording</span>
            </div>
          )}

          {audioUrl && !isRecording && (
            <button
              className="upload-button"
              onClick={uploadRecording}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader size={20} className="spin" /> Uploading...
                </>
              ) : (
                <>
                  <Upload size={20} /> Upload Recording
                </>
              )}
            </button>
          )}
        </div>

        {error && (
          <div className={`message ${error.type === 'success' ? 'success-message' : 'error-message'}`}>
            {typeof error === 'string' ? error : error.message}
          </div>
        )}

        {audioUrl && !isRecording && (
          <div className="audio-preview">
            <audio src={audioUrl} controls />
          </div>
        )}
      </div>

      <div className="previous-doubts">
        <h2>Your Previous Doubts</h2>
        {previousDoubts.length > 0 ? (
          <ul className="doubts-list">
            {previousDoubts.map((doubt) => (
              <li key={doubt.id} className="doubt-item">
                <div className="doubt-info">
                  <span className="doubt-date">{formatDate(doubt.createdAt)}</span>
                </div>
                <div className="audio-controls">
                  <button
                    className="play-button"
                    onClick={() => handlePlayPause(doubt.audioUrl)}
                    aria-label={currentlyPlaying === doubt.audioUrl ? "Pause" : "Play"}
                  >
                    {currentlyPlaying === doubt.audioUrl ? (
                      <Pause size={20} />
                    ) : (
                      <Play size={20} />
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No previous doubts found.</p>
        )}
      </div>
    </div>
  );
};

export default Doubts; 
